'use client';

import React from 'react';
import { useForm } from 'react-hook-form';
import type { LeadData } from './PitchDeck';
import { ArrowRight, ShieldCheck } from 'lucide-react';

interface Props {
  onNext: (data: LeadData) => void;
}

export default function Slide1LeadCapture({ onNext }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LeadData>();

  const onSubmit = async (data: LeadData) => {
    const leadId = `lead-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    try {
      await fetch('/api/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'lead', data: { ...data, leadId } }),
      });
    } catch (err) {
      console.error('Lead notification failed:', err);
    }
    onNext({ ...data, leadId });
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      {/* Background radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(0,168,255,0.12) 0%, transparent 70%)',
        }}
      />

      {/* Headlines */}
      <div className="text-center mb-10 max-w-2xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight mb-4">
          Discover the Science of{' '}
          <span className="gradient-text">X39</span>
        </h1>
      </div>

      {/* Form */}
      <div className="glass-card w-full max-w-lg p-8 glow-blue">

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                First Name
              </label>
              <div className="relative">
                <input
                  {...register('firstName', { required: 'First name is required' })}
                  className="input-field"
                />
              </div>
              {errors.firstName && (
                <p className="text-red-400 text-xs mt-1">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Last Name
              </label>
              <div className="relative">
                <input
                  {...register('lastName', { required: 'Last name is required' })}
                  className="input-field"
                />
              </div>
              {errors.lastName && (
                <p className="text-red-400 text-xs mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Mobile Number
            </label>
            <div className="relative">
              <input
                {...register('phone', {
                  required: 'Mobile number is required',
                  pattern: { value: /^[\d\s\-+()]{7,}$/, message: 'Enter a valid phone number' },
                })}
                className="input-field"
                type="tel"
              />
            </div>
            {errors.phone && (
              <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                })}
                className="input-field"
                type="email"
              />
            </div>
            {errors.email && (
              <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full flex items-center justify-center gap-2 mt-2"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : (
              <>
                Begin
                <ArrowRight size={18} />
              </>
            )}
          </button>
          <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5 mt-2">
            <ShieldCheck size={13} className="text-blue-400" />
            Your information is encrypted and secure
          </p>
        </form>
      </div>

      {/* Trust badges */}
      <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
          100+ Countries
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          Patented Technology
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          No Drugs or Chemicals
        </span>
      </div>
    </div>
  );
}